import { RendererProps } from '../types'
import Context from './AppContext'
import React, { useContext, ComponentType, useEffect, useState, useCallback, useRef, MutableRefObject } from 'react'
import { ErrorBoundary, DeadComp } from './ErrorBoundary'
import { getDisplayedId } from '@wix/thunderbolt-commons'
import { IPropsStore, PropsMap } from '@wix/thunderbolt-symbols'

type StructureComponentProps = { id: string; displayedItemId?: string }

const fixEventProp = (func: (...args: any) => any, displayedId: string) => {
	return (event: any, ...rest: Array<any>) => {
		if (event?.nativeEvent instanceof Event) {
			event.compId = displayedId
		}
		return func(event, ...rest)
	}
}

const getProps = (displayedId: string, id: string, propsStore: IPropsStore) => {
	const props = propsStore.get(id)
	if (displayedId === id) {
		return props
	}

	const itemProps = propsStore.get(displayedId)
	const fixedProps = Object.entries(props).reduce((acc, [prop, value]) => {
		if (!itemProps?.hasOwnProperty(prop)) {
			acc[prop] = typeof value === 'function' ? fixEventProp(value, displayedId) : value
		}
		return acc
	}, {} as PropsMap)

	if (Object.keys(fixedProps).length) {
		propsStore.update({ [displayedId]: fixedProps })
	}

	// Get the updated value from the propsStore that includes all props
	return propsStore.get(displayedId)
}

const withObserver = (Comp: ComponentType<StructureComponentProps>): ComponentType<StructureComponentProps> => ({
	id,
	displayedItemId = '',
}) => {
	const { structure: structureStore, props: propsStore, compsLifeCycle } = useContext(Context)
	const displayedId = displayedItemId ? getDisplayedId(id, displayedItemId) : id

	const [, setTick] = useState(0)
	const forceUpdate = useCallback(() => setTick((tick) => tick + 1), [])
	const registerOnMount = () => {
		const stores = [propsStore, structureStore]
		const unSubscribers: Array<() => void> = []
		compsLifeCycle.notifyCompDidMount(id, displayedId)
		stores.forEach((store) => {
			const unsubscribe = store.subscribeById(displayedId, forceUpdate)
			unSubscribers.push(unsubscribe)
			if (displayedId !== id) {
				unSubscribers.push(store.subscribeById(id, forceUpdate))
			}
		})

		return () => {
			unSubscribers.forEach((cb) => cb())
		}
	}

	useEffect(registerOnMount, [])

	return <Comp id={id} displayedItemId={displayedItemId} />
}

const StructureComponent: ComponentType<StructureComponentProps> = withObserver(({ id, displayedItemId = '' }) => {
	const {
		structure: structureStore,
		props: propsStore,
		comps,
		translate,
		logger,
		compEventsRegistrar,
		compControllers,
		createCompControllerArgs,
	}: RendererProps = useContext(Context)
	const displayedId = displayedItemId ? getDisplayedId(id, displayedItemId) : id

	const compStructure = structureStore.get(displayedId) || structureStore.get(id)
	const { componentType, uiType } = compStructure
	const compClassType = uiType ? `${componentType}_${uiType}` : componentType
	const Comp = comps[compClassType]

	if (compControllers[compClassType]) {
		const controllerActionsReference: MutableRefObject<any> = useRef(null)
		if (!controllerActionsReference.current) {
			controllerActionsReference.current = compControllers[compClassType](createCompControllerArgs(displayedId))
		}

		compEventsRegistrar.registerController(displayedId, controllerActionsReference.current)
	}

	const compProps = getProps(displayedId, id, propsStore) || {}
	const components = compStructure!.components

	const children = useCallback(
		(itemId?: string) =>
			(components || []).map((childId: string) => {
				const childProps = propsStore.get(childId)

				return (
					<StructureComponent
						displayedItemId={displayedItemId || itemId}
						id={childId}
						key={childProps?.key || childId}
					/>
				)
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[components, displayedItemId]
	)

	// TODO: Remove the fallback once all components are implemented
	const component = Comp ? (
		<Comp translate={translate} {...compProps} id={displayedId}>
			{children}
		</Comp>
	) : (
		<DeadComp id={displayedId}>{children()}</DeadComp>
	)

	return (
		<ErrorBoundary
			id={displayedId}
			logger={logger}
			recursiveChildren={children}
			Component={Comp}
			compClassType={compClassType}
			sentryDsn={compProps.sentryDsn}
		>
			{component}
		</ErrorBoundary>
	)
})

export default StructureComponent
